import classNames from 'classnames';
import React from 'react';
import { List, WindowScrollerChildProps } from 'react-virtualized';

import { CrudActivityContext } from '@futrli/shared-services';

import { PredictionsAccountCategories, VIEW_TYPES } from '../../../../core/types';
import { UserGridSettingsContextType } from '../user-grid-settings/types';
import { withUserGridSettingsContext } from '../user-grid-settings/user-grid-settings-context';
import { getRowHeightSize } from '../user-grid-settings/user-grid-settings-actions';
import { IDataGridContext, withDataGridContext } from './context/data-grid';
import { HeaderRowSwitch } from './headers/header-row-switch';
import { getRowHeight, getWrapperStyle } from './style-rows';
import { HeightProps, RenderRowProps, RowsType, RowType } from './types';
import grid from '../../../../ui/data-grid/components/grid.scss';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { INITIAL_ROW_HEADER_WIDTH } from '../../../../core/constants';
import { LDProps } from '../../../../../../skeleton/src/core/providers/launch-darkly';
import { PredictionBuilderContainer } from '../../../../forecast/prediction-builder/containers/prediction-builder-container';

interface OwnProps {
  rows: RowsType;
  windowScrollerChildProps: WindowScrollerChildProps;
  isPayments: boolean;
  category?: PredictionsAccountCategories;
  viewType?: VIEW_TYPES;
  rowHeaderWidth?: number;
  onListContainerMouseDown: (event: { clientX: number; clientY: number }) => void;
  listContainerRef: React.RefObject<HTMLDivElement>;
  isBudget?: boolean;
  ariaLabel?: string;
}

type UserGridSettingsContextProps = Pick<UserGridSettingsContextType, 'rowHeights'>;

type Props = IDataGridContext & OwnProps & UserGridSettingsContextProps & LDProps;

interface State {
  isOpenManualPredictionDrawer: boolean;
  predictionId?: string;
  predictionType?: string;
  isQuickCopy: boolean;
}

export class VirtualizedHeaderRowsComponent extends React.PureComponent<Props> {
  public state: State = {
    isOpenManualPredictionDrawer: false,
    predictionId: undefined,
    predictionType: undefined,
    isQuickCopy: false
  };

  public handleCloseManualPredictionDrawer = () => {
    this.setState({
      isOpenManualPredictionDrawer: false,
      predictionId: undefined,
      predictionType: undefined,
      isQuickCopy: false
    });
  };

  public openManualPredictionDrawer = (predictionId?: string, predictionType?: string, isQuickCopy?: boolean) => {
    this.setState({
      isOpenManualPredictionDrawer: true,
      predictionId,
      predictionType,
      isQuickCopy
    });
  };

  private isVirtualizationDisabled = (): boolean => {
    const params = new URLSearchParams(window.location.search);
    return params.has('disableVirtualisation');
  };

  public render(): JSX.Element {
    const {
      rowHeights,
      rows,
      windowScrollerChildProps,
      rowHeaderWidth,
      onListContainerMouseDown,
      listContainerRef,
      ariaLabel
    } = this.props;
    const { predictionId, predictionType, isQuickCopy, isOpenManualPredictionDrawer } = this.state;
    const { height, isScrolling, onChildScroll, scrollTop } = windowScrollerChildProps;
    const rowHeight = getRowHeightSize(rowHeights);

    const rowHeaderWrapperClasses = classNames({
      [grid.rowHeaderWrapper]: true,
      [grid.hasScrolled]: true
    });

    const disableVirtualization = this.isVirtualizationDisabled();

    return (
      <div
        className={rowHeaderWrapperClasses}
        style={{ width: rowHeaderWidth || INITIAL_ROW_HEADER_WIDTH }}
        ref={listContainerRef}
        data-testid="datagrid-headers-table"
      >
        {disableVirtualization ? (
          <div className={grid.virtualizedList}>
            {rows.map((row, index) => {
              const rowHeightValue = this.getRowHeight({ index });
              const style = { height: rowHeightValue };
              return this.rowRenderer({
                index,
                key: `row-${index}`,
                style,
                isScrolling: false,
                isVisible: true
              });
            })}
          </div>
        ) : (
          <List
            role="grid"
            aria-label={ariaLabel || ''}
            key={`${rowHeight}-${rows.length}`}
            autoHeight={true}
            itemData={rows}
            isScrolling={isScrolling}
            onScroll={onChildScroll}
            scrollTop={scrollTop}
            className={grid.virtualizedList}
            height={height}
            rowCount={rows.length}
            rowHeight={this.getRowHeight}
            width={rowHeaderWidth || INITIAL_ROW_HEADER_WIDTH}
            rowRenderer={this.rowRenderer}
          />
        )}
        <div className={grid.resizeHandle} onMouseDown={onListContainerMouseDown} />
        {isOpenManualPredictionDrawer && (
          <CrudActivityContext>
            <PredictionBuilderContainer
              urlPredictionId={predictionId}
              urlPredictionType={predictionType}
              isQuickCopy={isQuickCopy}
              onClose={this.handleCloseManualPredictionDrawer}
            />
          </CrudActivityContext>
        )}
      </div>
    );
  }

  private rowRenderer = ({ index, key, style }: RenderRowProps) => {
    const { rows, isPayments, category, viewType } = this.props;
    const row = rows[index];
    const firstIndexOfAssumption = rows.findIndex(r => r.kind === 'assumption');

    return (
      <HeaderRowSwitch
        key={key}
        row={row}
        level={row.level}
        wrapperStyle={this.getWrapperStyle(index, style, row)}
        isPayments={isPayments}
        category={category}
        viewType={viewType}
        isFirstAssumption={firstIndexOfAssumption === index}
        onManualPredictionDrawerOpen={this.openManualPredictionDrawer}
      />
    );
  };

  private getWrapperStyle = (index: number, style: React.CSSProperties, row: RowType) => {
    const { rowHeights } = this.props;
    const rowHeight = getRowHeightSize(rowHeights);

    return getWrapperStyle(index, style, rowHeight, row, true);
  };

  private getRowHeight = ({ index }: HeightProps) => {
    const { rowHeights, rows } = this.props;

    return getRowHeight(index, rowHeights, rows[index], true);
  };
}

const HeaderRowsVirtualizedWithDataGridContext = withDataGridContext<
  OwnProps & UserGridSettingsContextProps,
  IDataGridContext
>(VirtualizedHeaderRowsComponent);

export const HeaderRowsVirtualized = withLDConsumer()(
  withUserGridSettingsContext<OwnProps, UserGridSettingsContextProps>(HeaderRowsVirtualizedWithDataGridContext, [
    'rowHeights'
  ])
);
